import React, { useEffect, useState } from 'react';
import { X, Download, Share, Plus } from 'lucide-react';

const DISMISS_KEY = 'r32_pwa_dismissed_at';
const DISMISS_DAYS = 14;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isIosSafari = () =>
  isIos() && /safari/i.test(navigator.userAgent) && !/crios|fxios|edgios/i.test(navigator.userAgent);

const recentlyDismissed = () => {
  const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return at > 0 && Date.now() - at < DISMISS_DAYS * 86400000;
};

export const InstallAppPrompt = () => {
  const [promptEvent, setPromptEvent] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [showIosSteps, setShowIosSteps] = useState(false);

  useEffect(() => {
    const forced = new URLSearchParams(window.location.search).get('pwa') === 'test';
    if (!forced && (isStandalone() || recentlyDismissed())) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setPromptEvent(e);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    // iOS nu suportă beforeinstallprompt: afișăm instrucțiunile din Safari, după puțin timp.
    let timer;
    if (isIos() || forced) timer = setTimeout(() => setShowIos(true), forced ? 300 : 4000);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setPromptEvent(null);
    setShowIos(false);
    setShowIosSteps(false);
  };

  const install = async () => {
    if (!promptEvent) return;
    promptEvent.prompt();
    await promptEvent.userChoice;
    dismiss();
  };

  if (!promptEvent && !showIos) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-20 z-[60] md:inset-x-auto md:right-6 md:bottom-6 md:w-96"
      data-testid="pwa-install-banner"
    >
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white/95 p-4 shadow-2xl backdrop-blur">
        <button
          onClick={dismiss}
          aria-label="Închide"
          className="absolute right-2 top-2 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          data-testid="pwa-dismiss-btn"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <img src="/icon-192.png" alt="R32.ro" className="h-12 w-12 flex-shrink-0 rounded-xl" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900">Instalează aplicația R32.ro</p>
            <p className="mt-0.5 text-xs leading-relaxed text-gray-600">
              Iconiță pe ecranul principal, pornire instantanee și acces rapid la comenzile tale.
            </p>
          </div>
        </div>

        {promptEvent ? (
          <button
            onClick={install}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700"
            data-testid="pwa-install-btn"
          >
            <Download className="h-4 w-4" /> Instalează
          </button>
        ) : showIosSteps ? (
          <ol className="mt-3 space-y-2 text-xs text-gray-700" data-testid="pwa-ios-steps">
            <li className="flex items-center gap-2">
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-[10px] font-bold">1</span>
              Apasă <Share className="mx-0.5 inline h-3.5 w-3.5" /> (Partajare) în bara Safari
            </li>
            <li className="flex items-center gap-2">
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-[10px] font-bold">2</span>
              Alege <Plus className="mx-0.5 inline h-3.5 w-3.5" /> „Adaugă pe ecranul principal”
            </li>
            <li className="flex items-center gap-2">
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-[10px] font-bold">3</span>
              Confirmă cu „Adaugă” — gata!
            </li>
            {!isIosSafari() && (
              <li className="rounded-lg bg-amber-50 p-2 text-[11px] text-amber-800">
                Pe iPhone instalarea merge doar din Safari. Deschide r32.ro în Safari și reia pașii.
              </li>
            )}
          </ol>
        ) : (
          <button
            onClick={() => setShowIosSteps(true)}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700"
            data-testid="pwa-ios-howto-btn"
          >
            <Download className="h-4 w-4" /> Cum o instalez?
          </button>
        )}
      </div>
    </div>
  );
};

export default InstallAppPrompt;
